import React, { useEffect, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import PassportSwitcher from 'components/PassportSwitcher'
import PassportEmblem from 'components/PassportEmblem'
import ComplianceChip from 'components/PassportHeader/ComplianceChip'
import { ink, line, radius, surface, font, transition, bp as bpToken } from 'features/dashboard/tokens'

const TONE_DOT = {
  good: '#15803d',
  warn: '#d97706',
  bad: '#b00020',
  neutral: '#8A8F9C',
}

const KIND_META = {
  building: { label: 'Building Passport', tint: '#9c3419' },
  portfolio: { label: 'Portfolio Passport', tint: '#1f8c97' },
  estate: { label: 'Estate Passport', tint: '#4c3a99' },
}

const PassportHeader = ({
  kind = 'building',
  name,
  subtitle,
  onSelectBp,
  actions,
  meta = [],
  toolbar,
  complianceScope,
  onOpenCompliance,
  complianceOpenLabel,
  className,
}) => {
  const sentinelRef = useRef(null)
  const [condensed, setCondensed] = useState(false)
  const [barTop, setBarTop] = useState(0)
  const barRef = useRef(null)
  const cardRef = useRef(null)
  const [modalOpen, setModalOpen] = useState(false)
  const theme = useTheme()
  const tokenTopBar = parseInt(theme?.dimensions?.topBarHeight, 10) || 0

  useEffect(() => {
    const measure = () => {
      const bar = document.querySelector('[data-app-topbar]')
      const height = bar ? Math.round(bar.getBoundingClientRect().height) : 0
      setBarTop(height > 0 ? height : tokenTopBar)
    }
    measure()
    const raf = window.requestAnimationFrame(measure)
    const settle = window.setTimeout(measure, 300)
    window.addEventListener('resize', measure)
    const bar = document.querySelector('[data-app-topbar]')
    const observer = typeof ResizeObserver !== 'undefined' && bar ? new ResizeObserver(measure) : null
    if (observer) observer.observe(bar)
    return () => {
      window.cancelAnimationFrame(raf)
      window.clearTimeout(settle)
      window.removeEventListener('resize', measure)
      if (observer) observer.disconnect()
    }
  }, [tokenTopBar])

  useEffect(() => {
    const node = cardRef.current
    if (!node) return undefined
    let frame = null
    const evaluate = () => {
      frame = null
      const rect = node.getBoundingClientRect()
      setCondensed(rect.bottom < barTop + 8)
    }
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(evaluate)
    }
    evaluate()
    const observer = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(schedule, { rootMargin: '-8px 0px 0px 0px', threshold: 0 })
      : null
    if (observer) observer.observe(node)
    const resizer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null
    if (resizer) resizer.observe(document.body)
    window.addEventListener('scroll', evaluate, { passive: true })
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      if (observer) observer.disconnect()
      if (resizer) resizer.disconnect()
      window.removeEventListener('scroll', evaluate)
    }
  }, [barTop])

  useEffect(() => {
    const root = document.documentElement
    const apply = () => {
      const barHeight = condensed && barRef.current ? barRef.current.getBoundingClientRect().height : 0
      root.style.setProperty('--passport-bar-bottom', `${Math.round(barTop + barHeight)}px`)
    }
    apply()
    window.addEventListener('resize', apply)
    return () => {
      window.removeEventListener('resize', apply)
      root.style.removeProperty('--passport-bar-bottom')
    }
  }, [condensed, barTop])

  useEffect(() => {
    const check = () => setModalOpen(document.body.style.position === 'fixed')
    check()
    const observer = new MutationObserver(check)
    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] })
    return () => observer.disconnect()
  }, [])

  const scrollToTop = () => {
    const node = sentinelRef.current
    if (node && node.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'start' })
    else window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <>
      <Sentinel ref={sentinelRef} aria-hidden='true' />
      {condensed && !modalOpen && (
        <StuckBar ref={barRef} $top={barTop} data-tour='passport-stuck-bar'>
          <StuckInner>
            <StuckJump
              type='button'
              onClick={scrollToTop}
              title='Back to the top of this Passport'
            >
              <PassportEmblem kind={kind} size='sm' showLabel={false} />
              <StuckText>
                <StuckKind $tint={(KIND_META[kind] || KIND_META.building).tint}>
                  {(KIND_META[kind] || KIND_META.building).label}
                </StuckKind>
                <StuckName>{name}</StuckName>
                {subtitle && <StuckSub>{subtitle}</StuckSub>}
              </StuckText>
              <StuckArrow aria-hidden='true'><ArrowUpwardRoundedIcon style={{ fontSize: 16 }} /></StuckArrow>
            </StuckJump>
            {actions && <StuckActions>{actions}</StuckActions>}
          </StuckInner>
        </StuckBar>
      )}
    <Card ref={cardRef} className={className} data-tour='passport-card' data-tour-id='passport.card'>
      <TopRow>
        <IdentityBox data-tour='passport-identity' data-tour-id='passport.identity'>
          <PassportSwitcher kind={kind} name={name} subtitle={subtitle} meta={meta} onSelectBp={onSelectBp} />
        </IdentityBox>
        {(actions || toolbar) && (
          <Controls>
            {actions && <Actions data-tour='passport-actions' data-tour-id='passport.actions'>{actions}</Actions>}
            {toolbar && <Toolbar data-tour='passport-visibility' data-tour-id='passport.visibility'>{toolbar}</Toolbar>}
          </Controls>
        )}
      </TopRow>
      {complianceScope && (
        <ComplianceRow>
          <ComplianceChip scope={complianceScope} onOpen={onOpenCompliance} openLabel={complianceOpenLabel || (kind === 'building' ? 'Open compliance' : 'Open overview')} />
        </ComplianceRow>
      )}
    </Card>
    </>
  )
}

const PassportFacts = ({ fields }) => {
  const shown = fields.filter(f => f && f.value !== null && f.value !== undefined && f.value !== '')
  const stats = shown.filter(f => !f.wrap)
  const wraps = shown.filter(f => f.wrap)
  if (shown.length === 0) return null

  return (
    <DeckMain>
      {stats.length > 0 && (
        <Stats data-tour='passport-stats' data-tour-id='passport.stats'>
          {stats.map(f => {
            const big = String(f.value).length <= 8
            const Icon = f.icon
            const inner = (
              <>
                {f.emblem && <PassportEmblem kind={f.emblem} size='xs' showLabel={false} tone={f.emblemTone || 'solid'} />}
                {!f.emblem && Icon && <StatIcon aria-hidden='true'><Icon style={{ fontSize: 17 }} /></StatIcon>}
                <StatBody>
                  <StatValue $big={big} $accent={f.accent} data-accent={f.accent || undefined}>
                    {f.tone && <Dot $tone={f.tone} />}
                    <span>{f.value}</span>
                    {f.detail && <StatDetail $accent={f.accent}>{f.detail}</StatDetail>}
                  </StatValue>
                  <StatLabel>
                    <span>
                      {f.label}
                      {f.onClick && <StatArrow aria-hidden='true'>&nbsp;→</StatArrow>}
                    </span>
                  </StatLabel>
                </StatBody>
              </>
            )
            return f.onClick ? (
              <StatTile
                key={f.label}
                as='button'
                type='button'
                $clickable
                onClick={f.onClick}
                title={f.title || `Open ${f.label.toLowerCase()}`}
              >
                {inner}
              </StatTile>
            ) : (
              <StatTile key={f.label} title={f.title}>{inner}</StatTile>
            )
          })}
        </Stats>
      )}
      {wraps.map(f => {
        const Icon = f.icon
        return (
          <Prose key={f.label}>
            <StatLabel>
              {Icon && <Icon style={{ fontSize: 14 }} aria-hidden='true' />}
              {f.label}
            </StatLabel>
            <ProseText>{f.value}</ProseText>
          </Prose>
        )
      })}
    </DeckMain>
  )
}

export const PassportOverviewIntro = ({ highlight, fields = [], className }) => {
  const facts = <PassportFacts fields={fields} />
  const hasFacts = fields.some(f => f && f.value !== null && f.value !== undefined && f.value !== '')
  if (!highlight && !hasFacts) return null

  return (
    <IntroCard className={className} data-tour='passport-overview-intro'>
      {highlight && <Highlight data-tour='passport-pulse' data-tour-id='passport.pulse'>{highlight}</Highlight>}
      {highlight && hasFacts && <Rule />}
      {hasFacts && facts}
    </IntroCard>
  )
}

export default PassportHeader

const Card = styled.div`
  box-sizing: border-box;
  background: ${surface.card};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  box-shadow: 0 1px 2px rgba(12, 18, 41, 0.04);
  padding: 14px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  min-width: 0;

  @media (max-width: ${bpToken.mobile}) {
    padding: 12px;
    gap: 10px;
  }
`

const IntroCard = styled.div`
  box-sizing: border-box;
  background: ${surface.card};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  padding: 14px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-width: 0;

  @media (max-width: ${bpToken.mobile}) {
    padding: 12px;
  }
`

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 24px;
  flex-wrap: wrap;
  min-width: 0;
`

const IdentityBox = styled.div`
  display: flex;
  min-width: 0;
  flex: 0 1 auto;
`

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex: 0 1 auto;
  max-width: 100%;
  min-width: 0;

  @media (max-width: ${bpToken.mobile}) {
    align-items: flex-start;
    width: 100%;
  }
`

const Actions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 1 auto;
  flex-wrap: wrap;
  max-width: 100%;
  min-width: 0;

  @media (max-width: ${bpToken.mobile}) {
    width: 100%;
  }
`

const Rule = styled.div`
  height: 1px;
  background: ${line.base};
`






const Dot = styled.span`
  align-self: center;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $tone }) => TONE_DOT[$tone] || TONE_DOT.neutral};
`


const DeckMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`


const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1px;
  overflow: hidden;
  border-radius: 12px;
  background: ${line.base};

  @media (max-width: ${bpToken.mobile}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const ACCENT = {
  good: '#15803d',
  warn: '#d97706',
  bad: '#b00020',
}

const StatValue = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 8px;
  row-gap: 2px;
  min-width: 0;
  max-width: 100%;
  min-height: 26px;
  font-size: ${({ $big }) => ($big ? '24px' : '16px')};
  font-weight: 600;
  letter-spacing: ${({ $big }) => ($big ? '-0.01em' : '0')};
  line-height: ${({ $big }) => ($big ? '1.1' : '1.35')};
  color: ${({ $accent }) => ACCENT[$accent] || ink.base};
  font-variant-numeric: tabular-nums;
  transition: ${transition};

  > span {
    flex: 0 0 auto;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: ${bpToken.mobile}) {
    > span {
      white-space: normal;
      overflow-wrap: anywhere;
    }
  }
`

const StatDetail = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1.2;
  color: ${({ $accent }) => ACCENT[$accent] || ink.soft};
`

const ComplianceRow = styled.div`
  margin-top: 10px;
  display: flex;
  min-width: 0;
`

const StatLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0;
  color: ${ink.mute};
  white-space: normal;
  line-height: 1.3;

  > span {
    min-width: 0;
  }
`

const StatArrow = styled.span`
  font-size: 12px;
  letter-spacing: 0;
  opacity: 0.6;
  white-space: nowrap;
`

const StatIcon = styled.span`
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: ${surface.card};
  color: ${ink.soft};
  border: 1px solid ${line.base};

  @media (max-width: ${bpToken.mobile}) {
    width: 26px;
    height: 26px;
  }
`

const StatBody = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  min-width: 0;
`

const StatTile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  max-width: 100%;
  padding: 12px 18px;
  border: none;
  border-radius: 0;
  background: ${surface.wash};
  font-family: inherit;
  text-align: left;
  color: inherit;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: ${transition};

  & > :first-child {
    flex-shrink: 0;
  }

  &:hover {
    background: ${({ $clickable }) => ($clickable ? surface.card : surface.wash)};
  }

  @media (max-width: ${bpToken.mobile}) {
    gap: 8px;
    padding: 8px 10px;

    &:last-child:nth-child(odd) {
      grid-column: 1 / -1;
    }
  }

  &:hover ${StatValue}:not([data-accent]) {
    color: ${({ $clickable }) => ($clickable ? '#1f8c97' : ink.base)};
  }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }
`

const Prose = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const ProseText = styled.p`
  margin: 0;
  max-width: 720px;
  font-size: ${font.body};
  line-height: 1.5;
  color: ${ink.soft};
  overflow-wrap: anywhere;
`






const Highlight = styled.div`
  min-width: 0;
`

const Sentinel = styled.span`
  display: block;
  height: 1px;
  margin-bottom: -1px;
`




const StuckBar = styled.div`
  position: fixed;
  top: ${({ $top }) => $top || 0}px;
  left: ${({ theme }) => `var(--nav-rail-width, ${theme.dimensions.navWidth})`};
  right: 0;
  z-index: 900;
  display: flex;
  justify-content: center;
  padding: 6px 24px;
  background: ${surface.card};
  border-bottom: 1px solid ${line.base};
  box-shadow: 0 6px 18px -14px rgba(12, 18, 41, 0.45);

  @media (max-width: ${bpToken.mobile}) {
    left: 0;
    display: none;
  }
`

const StuckInner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 1400px;
  min-width: 0;
`

const StuckJump = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 3px;
    border-radius: 6px;
  }
`

const StuckText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`

const StuckKind = styled.span`
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  line-height: 1;
  color: ${({ $tint }) => $tint || '#1f8c97'};
`

const StuckSub = styled.span`
  font-size: ${font.small};
  color: ${ink.mute};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StuckArrow = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${ink.mute};
  opacity: 0;
  transition: ${transition};

  ${StuckJump}:hover &, ${StuckJump}:focus-visible & { opacity: 1; }
`

const StuckName = styled.span`
  min-width: 0;
  font-size: ${font.emphasis};
  font-weight: 700;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StuckActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 0;
  max-width: 100%;

  @media (max-width: ${bpToken.mobile}) {
    justify-content: flex-start;
  }
`




export const PassportAction = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 14px;
  border-radius: ${radius.pill};
  border: 1px solid #e5e7eb;
  background: ${surface.card};
  color: #475569;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: ${transition};

  svg { width: 15px; height: 15px; flex-shrink: 0; }

  &:hover {
    border-color: #33becc;
    color: #33becc;
  }

  &:hover svg g { fill: #33becc; }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }
`

export const PassportDangerAction = styled(PassportAction)`
  &:hover {
    border-color: #b00020;
    color: #b00020;
  }
`

export const PassportIconAction = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: ${radius.pill};
  border: 1px solid #e5e7eb;
  background: ${surface.card};
  cursor: pointer;
  overflow: visible;
  transition: ${transition};

  &:hover { border-color: #33becc; }

  svg { width: 17px; height: 17px; display: block; }
`

const CHIP_TONE = {
  good: { bg: '#dcfce7', fg: '#15803d' },
  warn: { bg: '#fef3c7', fg: '#d97706' },
  bad: { bg: '#fdecea', fg: '#b00020' },
  neutral: { bg: '#eef0f3', fg: '#475569' },
}

export const PassportChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  padding: 0 12px;
  border: none;
  border-radius: ${radius.pill};
  background: ${({ $tone }) => (CHIP_TONE[$tone] || CHIP_TONE.neutral).bg};
  color: ${({ $tone }) => (CHIP_TONE[$tone] || CHIP_TONE.neutral).fg};
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  text-align: left;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
  transition: ${transition};

  svg { flex-shrink: 0; }

  &:hover { filter: ${({ onClick }) => (onClick ? 'brightness(0.96)' : 'none')}; }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }
`

export const PassportMapThumb = styled.button`
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  border: 1px solid ${line.base};
  border-radius: 12px;
  overflow: hidden;
  background: ${surface.wash};
  cursor: pointer;
  transition: ${transition};

  &:hover { border-color: rgba(51, 190, 204, 0.7); }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }
`

export const PassportMapThumbLabel = styled.span`
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: ${radius.pill};
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 1px 2px rgba(12, 18, 41, 0.14);
  font-size: 11.5px;
  font-weight: 700;
  color: #1f8c97;
  pointer-events: none;
`
